import { Service } from "./restService";

class DeviceService extends Service {

    addDevice = (data) => {

        const url = 'device';
        const body = JSON.stringify({
            name: data.name,
            type: data.type,
            label: data.label || '',
            additionalInfo: {
                description: data.description || '',
                gateway: false
            }
        });

        return this.POST(url, body)
            .then(response => response);
    };

    addServerAttributes = (data, deviceId) => {

        const url = 'plugins/telemetry/DEVICE/' + deviceId + '/SERVER_SCOPE';
        const body = JSON.stringify(data);

        return this.POST(url, body)
            .then(response => response);
    };

    addDeviceEntity = (data, deviceId) => {

        const url = 'entityView';
        const dataType = data.type;
        let timeseries = [];

        if (dataType.slice(0, -3) === "acuadori node") {
            timeseries = [
                // Legacy keys kept for compatibility with devices already deployed.
                "data_AcuaNode_LeafSensor_B",
                "data_AcuaNode_LeafSensor_A",
                "data_AcuaNode_ClipSensor1_C",
                "data_AcuaNode_ClipSensor1_L",
                "data_AcuaNode_ClipSensor1_T",
                "data_AcuaNode_ClipSensor2_C",
                "data_AcuaNode_ClipSensor2_L",
                "data_AcuaNode_ClipSensor2_T",
                // Compact decoder metadata intentionally exposed to customer dashboards.
                "data_info_ts",
                "data_info_source",
                "data_info_battery_info_available",
                "data_info_battery_info_soc_pct",
                "data_info_battery_info_vcell_mv",
                "data_info_battery_info_present",
                "data_info_battery_info_source"
            ];

            const clampIds = ["A", "B", "C", "D"];
            const opticalKeys = [
                "optical_valid",
                "optical_warnings",
                "optical_red_voltage_v",
                "optical_red_photodiode_current_micro_a",
                "optical_red_saturated",
                "optical_infrared_voltage_v",
                "optical_infrared_photodiode_current_micro_a",
                "optical_infrared_saturated"
            ];
            const impedanceKeys = [
                "impedance_point_count",
                "impedance_calibrated",
                "impedance_valid",
                "impedance_warnings",
                "impedance_points_valid",
                "impedance_points_zero"
            ];

            clampIds.forEach((clampId) => {
                opticalKeys.forEach((key) => {
                    timeseries.push(`data_values_clamps_${clampId}_${key}`);
                });
                for (let warningIndex = 0; warningIndex < 4; warningIndex += 1) {
                    timeseries.push(`data_values_clamps_${clampId}_optical_warnings_${warningIndex}`);
                }
                impedanceKeys.forEach((key) => {
                    timeseries.push(`data_values_clamps_${clampId}_${key}`);
                });
                for (let warningIndex = 0; warningIndex < 3; warningIndex += 1) {
                    timeseries.push(`data_values_clamps_${clampId}_impedance_warnings_${warningIndex}`);
                }

                for (let pointIndex = 0; pointIndex < 20; pointIndex += 1) {
                    timeseries.push(`data_values_clamps_${clampId}_impedance_points_${pointIndex}`);
                }
            });
        }
        else if (dataType.slice(0, -3) === "acuadori meteo") {
            timeseries = [
                "Pressure",
                "outsideTemperature",
                "WindSpeed",
                "WindDirection",
                "TenMinutesAvgWindSpeed",
                "OutsideHumidity",
                "RainRate",
                "UV",
                "SolarRadiation",
                "DayRain",
                "DayET"
            ];
        }
        else {
            return Promise.reject(Error(`Unsupported device entity type: ${dataType}`));
        }

        const body = JSON.stringify({
            keys: {
                timeseries: timeseries,
                attributes: {
                    ss: ["coordinates", "latitude", "longitude"],
                    cs: [],
                    sh: []
                }
            },
            endTimeMs: 0,
            startTimeMs: 0,
            name: data.name,
            type: data.type,
            entityId: {
                entityType: "DEVICE",
                id: deviceId
            }
        });
        return this.POST(url, body)
            .then(response => response);
    };

    getDeviceById = (deviceId) => {

        const url = 'device/' + deviceId;

        return this.GET(url)
            .then(response => response);
    };

    getDeviceAttributesById = (deviceId) => {

        const url = 'plugins/telemetry/DEVICE/' + deviceId + '/values/attributes/SERVER_SCOPE';

        return this.GET(url)
            .then(response => response);
    };

    setRelationDeviceFromAsset = (deviceId, assetId) => {

        const url = 'relation';
        const body = JSON.stringify({
            to: { id: deviceId, entityType: "ENTITY_VIEW" },
            type: "Contains",
            from: { entityType: "ENTITY_VIEW", id: assetId },
            additionalInfo: null
        });

        return this.POST(url, body)
            .then(response => response);
    };

    getDevices = () => {
        const url = 'tenant/devices?pageSize=100&page=0&textSearch=';
        return this.GET(url)
            .then(response => response);
    }

    getCredentials = (deviceId) => {
        let url = 'device/' + deviceId + '/credentials';
        return this.GET(url)
            .then(response => response.credentialsId);
    }

    getTimeseriesKeys = (deviceId) => {
        // https://acuadori.abinsula.com/api/plugins/telemetry/DEVICE/b535c1a0-b494-11ea-9bda-dbe1f0ac55e3/keys/timeseries
        const url = 'plugins/telemetry/DEVICE/' + deviceId + '/keys/timeseries';
        return this.GET(url)
            .then(response => response);
    }

    getDeviceHistory = (deviceId, keys) => {
        // https://acuadori.abinsula.com/api/plugins/telemetry/DEVICE/$DEVICE_ID/values/timeseries?
        //limit=$LIMIT&agg=NONE&useStrictDataTypes=false&keys=$KEYS&startTs=$START_TS&endTs=$END_TS
        const subPath = 'plugins/telemetry/DEVICE/' + deviceId + '/values/timeseries';
        const keyChunks = [];
        const chunkSize = 10;

        for (let index = 0; index < keys.length; index += chunkSize) {
            keyChunks.push(keys.slice(index, index + chunkSize));
        }

        const requests = keyChunks.map((keyChunk) => {
            const query = {
                limit: 100000,
                agg: 'NONE',
                useStrictDataTypes: false,
                keys: keyChunk.join(','),
                startTs: 0,
                endTs: 9999999999999,
            };

            return this.QUERY(subPath, query);
        });

        return Promise.all(requests)
            .then((responses) => responses.reduce((history, response) => ({
                ...history,
                ...(response || {})
            }), {}));
    }
}

export default DeviceService;
export {
    DeviceService
}
